'use client';

import { createContext, useContext, useState, Dispatch, ReactNode, SetStateAction } from 'react';
import { Item, Menu, MenuFragment, ModifierFragment, OrderItem } from '@instamenu/context-checkout';
import { sum } from 'lodash-es';

type MenuItemContextType = {
  orderItem: OrderItem | undefined;
  price: number;
  item: Item;
  modifiers: ModifierFragment[];
  setQuantity: Dispatch<SetStateAction<number>>;
  quantity: number;
  instructions: string | undefined;
  modifierGroupErrors: string[];
  setModifierGroupErrors: Dispatch<SetStateAction<string[]>>;
  modifiersByGroupId: (id: string) => ModifierFragment[];
  setModifiersForGroup: (id: string, items: ModifierFragment[]) => void;
  setInstructions: Dispatch<SetStateAction<string | undefined>>;
  /** True when redeeming in a point-based loyalty program */
  isPointRedemption?: boolean;
  /** True when modifiers are included free in the reward */
  rewardIncludesModifiers?: boolean;
};

const MenuItemContext = createContext<MenuItemContextType | undefined>(undefined);

export type MenuItemProviderProps = {
  children: ReactNode;
  orderItem?: OrderItem;
  itemId: string;
  menu: MenuFragment;
  /** True when redeeming in a point-based loyalty program */
  isPointRedemption?: boolean;
  /** True when modifiers are included free in the reward */
  rewardIncludesModifiers?: boolean;
};

export const MenuItemProvider = (props: MenuItemProviderProps) => {
  const [menu] = useState(() => new Menu(props.menu));
  const { orderItem, children, isPointRedemption, rewardIncludesModifiers } = props;
  const item = menu.itemById(props.itemId);
  if (!item) throw new Error('Item not found in MenuItemProvider');

  const [quantity, setQuantity] = useState(orderItem?.quantity ?? 1);
  const [modifierGroupErrors, setModifierGroupErrors] = useState<string[]>([]);
  const [instructions, setInstructions] = useState(orderItem?.instructions);

  const mods =
    item.modifierGroups?.flatMap((group) =>
      group.modifiers.map((modifier) => ({ ...modifier, groupId: group.id })),
    ) ?? [];

  const [byGroup, setByGroup] = useState<Record<string, ModifierFragment[]>>(() => {
    const modifiersByGroupId = {} as Record<string, ModifierFragment[]>;
    orderItem?.modifiers.forEach((modifier) => {
      const matchingModifier = mods.find((mod) => mod.id === modifier.id);
      if (!matchingModifier) return;
      if (matchingModifier.outOfStock) return;
      modifiersByGroupId[matchingModifier.groupId] ||= [];
      modifiersByGroupId[matchingModifier.groupId].push(matchingModifier);
    });

    if (orderItem) return modifiersByGroupId;

    item.modifierGroups?.forEach((group) => {
      const defaultModifierId = group.defaultModifier?.id;
      if (!defaultModifierId) return;
      const matchingModifier = group.modifiers.find(
        (modifier) => modifier.id === defaultModifierId && !modifier.outOfStock,
      );
      if (!matchingModifier) return;
      modifiersByGroupId[group.id] = [matchingModifier];
    });

    return modifiersByGroupId;
  });

  const modifiers = Object.values(byGroup).flat();

  const price = ((item.price ?? 0) + sum(modifiers.map((modifier) => modifier.price))) * quantity;
  const modifiersByGroupId = (id: string) => {
    return byGroup[id] ?? [];
  };

  const setModifiersForGroup = (id: string, items: ModifierFragment[]) => {
    setByGroup((prev) => ({ ...prev, [id]: items.filter((item) => !item.outOfStock) }));
  };

  const value = {
    item,
    price,
    quantity,
    setQuantity,
    modifiers,
    modifierGroupErrors,
    setModifierGroupErrors,
    modifiersByGroupId,
    setModifiersForGroup,
    instructions,
    setInstructions,
    orderItem,
    isPointRedemption,
    rewardIncludesModifiers,
  };
  return <MenuItemContext.Provider value={value}>{children}</MenuItemContext.Provider>;
};

export const useMenuItemContext = () => useContext(MenuItemContext);
